import SyosEvents from './utils/SyosEvents';
import templateSeatInfoPopover from './templates/seat-info-popover-template';
import SyosUiContainer from './SyosUiContainer';
import contains from '../utils/dom/contains';
import { EventHandler } from '../utils/dom-events';
import getPopoverCoordinates from './utils/getPopoverCoordinates';
import DelayedAction from '../utils/DelayedAction';
import type SyosController from './SyosController';
import type SyosSeat from './SyosSeat';
import { IPriceTypeSelectionHelper } from './priceTypeSelectionHelpers/types';
import { ISyosLabels } from './labels/types';

const Key = {
  Escape: 27,
};

const SHOW_DELAY = 800;
const MOUSE_OUT_CLOSE_DELAY = 100;
const DISALLOW_OPEN_DURATION = 250;

interface MSSVGElementInstance {
  correspondingUseElement?: SVGUseElement;
}

enum HitState {
  Focused = 1,
  MouseIn,
  MouseOut,
}

const Class = {
  hitContainer: 'tn-syos-seat-info-popover__hit-container',
  content: 'tn-syos-seat-info-popover__content',
};

function getHtml() {
  return `
    <div class="${Class.hitContainer}">
      <div class="tn-syos-seat-info-popover__content-container">
        <kbd class="tn-syos-seat-info-popover__command" aria-hidden="true">Esc</kbd>
        <div class="${Class.content}"></div>
      </div>
    </div>`;
}

class SyosSeatInfoPopover extends SyosUiContainer {
  isOpenable = true;

  showDelay: DelayedAction;

  closeOnMouseOutDelay: DelayedAction;

  hitState?: HitState;

  disallowOpenDelay: DelayedAction;

  mouseMoveHandler: EventHandler;

  keyDownHandler: EventHandler;

  seat: SyosSeat | undefined;

  $hitContainer: HTMLElement;

  $content: HTMLElement;

  constructor(syosController: SyosController, private labels: ISyosLabels, private priceTypeSelectionHelper: IPriceTypeSelectionHelper) {
    super(syosController, '.tn-syos-seat-info-popover');

    this.$el?.html(getHtml());

    // eslint-disable-next-line prefer-destructuring
    this.$hitContainer = this.find(`.${Class.hitContainer}`)![0];

    // eslint-disable-next-line prefer-destructuring
    this.$content = this.find(`.${Class.content}`)![0];

    syosController.on(SyosEvents.SEAT_MOUSE_OVER, this.onSeatMouseOver);
    syosController.on(SyosEvents.SEAT_FOCUS, this.onSeatFocus);
    syosController.on(SyosEvents.SEAT_BLUR, this.hide);
    syosController.on(SyosEvents.SEAT_MAP_ADJUST, this.hide);
    syosController.on(SyosEvents.MODAL_CLOSE, this.temporarilyDisallowOpening);
    syosController.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.temporarilyDisallowOpening);

    this.closeOnMouseOutDelay = new DelayedAction(MOUSE_OUT_CLOSE_DELAY, {
      onTime: () => {
        if (this.hitState === HitState.MouseOut) {
          this.hide();
        }
      },
    });

    this.disallowOpenDelay = new DelayedAction(DISALLOW_OPEN_DURATION, {
      onReset: () => {
        this.hide();
        this.isOpenable = false;
      },
      onTime: () => {
        this.isOpenable = true;
      },
    });

    this.showDelay = new DelayedAction(SHOW_DELAY, {
      onTime: this.show,
    });

    this.mouseMoveHandler = new EventHandler(document, 'mousemove', this.onMouseMove);
    this.keyDownHandler = new EventHandler(document.body, 'keydown', this.onKeyDown);

    this.hide();
  }

  temporarilyDisallowOpening = () => {
    this.disallowOpenDelay.reset();
  };

  onSeatFocus = ({ seat }: { seat: SyosSeat; }) => {
    if (!this.isOpenable) {
      return;
    }

    this.hide();

    this.hitState = HitState.Focused;

    this.setupSeat(seat);
  };

  onSeatMouseOver = ({ seat }: { seat: SyosSeat; }) => {
    if (!this.isOpenable) {
      return;
    }

    if (this.seat === seat) {
      return;
    }

    this.hide();

    this.hitState = HitState.MouseIn;

    this.setupSeat(seat);
  };

  setupSeat = (seat: SyosSeat) => {
    this.seat = seat;

    this.mouseMoveHandler.start();
    this.closeOnMouseOutDelay.reset();
    this.showDelay.reset();
  };

  testIsElementInsideHitAreas($el: Node) {
    const { seat, $hitContainer } = this;

    if (contains($hitContainer, $el)) {
      return true;
    }

    if (seat && contains(seat.$el, $el)) {
      return true;
    }

    return false;
  }

  onMouseMove = (event: MouseEvent) => {
    let $target = event.target as Element;

    const { correspondingUseElement } = ($target as MSSVGElementInstance);

    if (correspondingUseElement) {
      $target = correspondingUseElement;
    }

    const isInside = this.testIsElementInsideHitAreas($target);
    const isOutside = !isInside;
    const { hitState } = this;
    const delayStopped = !this.closeOnMouseOutDelay.isRunning;

    if (hitState === HitState.Focused && isOutside) {
      this.hide();
      return;
    }

    if (hitState === HitState.MouseOut && delayStopped) {
      this.hide();
      return;
    }

    if (delayStopped && isOutside) {
      this.hide();
      return;
    }

    if (isInside) {
      this.closeOnMouseOutDelay.reset();
    }

    this.hitState = isInside
      ? HitState.MouseIn
      : HitState.MouseOut;
  };

  onKeyDown = (event: KeyboardEvent) => {
    const key = event.keyCode || event.charCode;

    if (key === Key.Escape) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.hide();
    }
  };

  show = () => {
    const { seat } = this;

    if (!seat) {
      return;
    }

    this.$content.innerHTML = templateSeatInfoPopover.render(this.labels, seat, this.priceTypeSelectionHelper);

    this.$el?.show();
    this.reposition();

    this.keyDownHandler.start();
  };

  hide = () => {
    this.mouseMoveHandler.stop();
    this.keyDownHandler.stop();

    this.closeOnMouseOutDelay.stop();
    this.showDelay.stop();

    this.seat = undefined;
    this.$el?.hide();
  };

  reposition() {
    const { seat, $hitContainer } = this;

    const seatRect = seat?.getClientRect();

    if (!seatRect) {
      return;
    }

    const popoverRect = $hitContainer.getBoundingClientRect();

    const { documentElement } = document;

    const container = {
      width: documentElement.clientWidth,
      height: documentElement.clientHeight,
    };

    const { x, y } = getPopoverCoordinates(seatRect, popoverRect, container);

    $hitContainer.style.left = `${Math.round(x)}px`;
    $hitContainer.style.top = `${Math.round(y)}px`;
  }
}

export default SyosSeatInfoPopover;
